import * as Sentry from '@sentry/browser'
import { ErrorBoundary } from '@sentry/react'
import { useCADPageStore, WAND } from '@/state'
import { Camera, Canvas } from '@react-three/fiber'
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { COLOR_OPTIONS } from '@/constants'

import { Scene } from '@/pages/CADPage/components/CADScene/CADScene'
import { eventManagerFactory } from '@/pages/CADPage/components/CADModel/hooks/useCanvasEventManager'
import { useCADQuery } from '@/services/queries/cads'
import { GLTFObject } from './GLTFObject'

let cachedWebGLSupport: boolean | null = null
const canInitializeWebGL = () => {
  if (cachedWebGLSupport !== null) return cachedWebGLSupport
  if (typeof document === 'undefined') return false
  try {
    const testCanvas = document.createElement('canvas')
    const context =
      testCanvas.getContext('webgl2') || testCanvas.getContext('webgl')
    const ok = Boolean(context)
    context?.getExtension?.('WEBGL_lose_context')?.loseContext?.()
    cachedWebGLSupport = ok
    return ok
  } catch {
    cachedWebGLSupport = false
    return false
  }
}

const handleWebGLError = (error: Error) => {
  Sentry.withScope((scope) => {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
    const debugInfo = gl?.getExtension('WEBGL_debug_renderer_info')

    scope.setContext('webgl', {
      vendor: debugInfo
        ? gl?.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : null,
      renderer: debugInfo
        ? gl?.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : null,
      maxTextureSize: gl?.getParameter(gl.MAX_TEXTURE_SIZE),
      contextLost: gl?.isContextLost(),
    })

    scope.setContext('device', {
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: 'deviceMemory' in navigator ? navigator.deviceMemory : null,
      memoryUsed: (performance as any).memory?.usedJSHeapSize,
      memoryLimit: (performance as any).memory?.jsHeapSizeLimit,
      visibilityState: document.visibilityState,
      sessionDuration: performance.now(),
      onLine: navigator.onLine,
    })
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
    Sentry.captureException(error)
  })
}

const WebGLFallback = () => (
  <div className="w-full h-full flex items-center justify-center rounded-md border bg-gray-50 p-4 text-sm text-gray-700">
    <div>
      <h1 className="text-lg mb-1">Could not initialize WebGL.</h1>
      <p className="mb-1">
        Make sure WebGL is enabled, or you might be using an unsupported
        configuration.
      </p>
      <p>
        1. Check &quot;Use Hardware Acceleration when available.&quot; under
        Chrome &gt; Settings &gt; Advanced Settings
      </p>
      <p>2. Restart your browser.</p>
      <p className="mt-1">
        Check the help pages{' '}
        <a
          href="https://q20help.notion.site/Quarter20-Spec-and-Computer-Requirements-2727d5cb96da809a894bcc9c50575de4"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-50"
        >
          here
        </a>
        .
      </p>
    </div>
  </div>
)

type CanvasContextType = {
  gltf: GLTFObject | undefined
  canvasID: string | null
  setCanvasID: (id: string | null) => void
  setGLTF: (gltf: GLTFObject | undefined) => void
  setCanvasBounds: (
    bounds: { x: number; y: number; width: number; height: number } | null,
  ) => void
  setCanvasVisible: (visible: boolean) => void
}

export const CanvasContext = createContext<CanvasContextType>({
  gltf: undefined,
  canvasID: null,
  setCanvasID: () => {},
  setGLTF: () => {},
  setCanvasBounds: () => {},
  setCanvasVisible: () => {},
})

export const CanvasCameraContext = createContext<Camera | null>(null)

export const useCanvasContext = () => useContext(CanvasContext)
export const useCanvasCamera = () => useContext(CanvasCameraContext)

export const CanvasProvider = ({ children }: { children: ReactNode }) => {
  const { data } = useCADQuery()
  const cadVersionId = data?.version?.id
  const isWebGLSupported = useMemo<boolean>(() => canInitializeWebGL(), [])

  const [canvasBounds, setCanvasBounds] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)
  const [canvasVisible, setCanvasVisible] = useState(false)
  const [hasMountedCanvas, setHasMountedCanvas] = useState(false)
  const [camera, setCamera] = useState<Camera | null>(null)
  const [canvasID, setCanvasID] = useState<string | null>(null)
  const wandSelected = useCADPageStore((state) => state.wandSelected)

  const gltfRef = useRef<GLTFObject | undefined>(undefined)

  const setGLTF = useCallback((gltf: GLTFObject | undefined) => {
    if (gltfRef.current && gltfRef.current !== gltf) {
      gltfRef.current.dispose()
    }
    gltfRef.current = gltf
  }, [])

  // Invalidate cache when cadVersionId changes
  useEffect(() => {
    if (cadVersionId && canvasID && cadVersionId !== canvasID) {
      if (gltfRef.current) {
        gltfRef.current.dispose()
        gltfRef.current = undefined
      }
      setCanvasID(cadVersionId)
    } else if (cadVersionId && !canvasID) {
      setCanvasID(cadVersionId)
    }
  }, [cadVersionId, canvasID])

  useEffect(() => {
    return () => {
      if (gltfRef.current) {
        gltfRef.current.dispose()
        gltfRef.current = undefined
      }
    }
  }, [])

  useEffect(() => {
    if (canvasVisible && canvasBounds) {
      setHasMountedCanvas(true)
    }
  }, [canvasVisible, canvasBounds])

  const contextValue = useMemo(
    () => ({
      get gltf() {
        return gltfRef.current
      },
      setCanvasBounds,
      setCanvasVisible,
      setGLTF,
      canvasID,
      setCanvasID,
    }),
    [setGLTF, canvasID],
  )

  const currentColorName = useMemo(() => {
    if (wandSelected === WAND.TRANSPARENCY) {
      return WAND.TRANSPARENCY
    }

    if (wandSelected) {
      const selectedColor = COLOR_OPTIONS.find(
        (color) => color.value === parseInt(wandSelected.slice(1), 16),
      )
      return selectedColor?.name
    }
  }, [wandSelected])

  useEffect(() => {
    if (hasMountedCanvas && !isWebGLSupported) {
      handleWebGLError(new Error('WebGL is not supported'))
    }
  }, [isWebGLSupported, hasMountedCanvas])

  return (
    <CanvasContext.Provider value={contextValue}>
      <div
        style={{
          position: 'fixed',
          left: canvasBounds?.x ?? 0,
          top: canvasBounds?.y ?? 0,
          width: canvasBounds?.width ?? 0,
          height: canvasBounds?.height ?? 0,
          visibility: canvasVisible && canvasBounds ? 'visible' : 'hidden',
          pointerEvents: canvasVisible && canvasBounds ? 'auto' : 'none',
          zIndex: 10,
        }}
      >
        {isWebGLSupported
          ? hasMountedCanvas && (
              <ErrorBoundary
                onError={(error) => handleWebGLError(error as Error)}
                fallback={<WebGLFallback />}
              >
                <Canvas
                  id="cad-canvas"
                  data-testid="cad-page-canvas"
                  events={eventManagerFactory}
                  className={
                    currentColorName ? `cursor-wand__${currentColorName}` : ''
                  }
                  fallback={<div>Loading...</div>}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  gl={{
                    toneMappingExposure: 0.8,
                    preserveDrawingBuffer: false,
                    localClippingEnabled: true,
                    antialias: true,
                    stencil: true,
                    powerPreference: 'default',
                    failIfMajorPerformanceCaveat: false,
                  }}
                  frameloop={canvasVisible ? 'demand' : 'never'}
                  performance={{ min: 0.5 }}
                  dpr={[1, 2]}
                  onCreated={({ gl }) => {
                    // Three r170 calls .trim() on nullable WebGL shader info logs.
                    gl.debug.checkShaderErrors = false
                  }}
                  onPointerDown={() =>
                    useCADPageStore.setState({ isRotating: true })
                  }
                  onPointerUp={() =>
                    useCADPageStore.setState({ isRotating: false })
                  }
                >
                  <Scene onCameraRef={setCamera} />
                </Canvas>
              </ErrorBoundary>
            )
          : hasMountedCanvas && <WebGLFallback />}
      </div>
      <CanvasCameraContext.Provider value={camera}>
        {children}
      </CanvasCameraContext.Provider>
    </CanvasContext.Provider>
  )
}

export default CanvasProvider
